import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { DayPicker, DayContentProps } from "react-day-picker"

import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"

export type CalendarProps = React.ComponentProps<typeof DayPicker>

/**
 * Custom DayContent component that renders a strikethrough for disabled dates
 * Uses inline styles for maximum reliability across different CSS contexts
 */
function CustomDayContent(props: DayContentProps) {
  const { date, activeModifiers } = props;
  const isMinStay = activeModifiers?.minStay;
  const isDisabled = activeModifiers?.disabled;

  // Min-stay-violating checkout candidates: dimmed but NOT struck-through,
  // so they read differently from booked/unavailable dates
  const styles: React.CSSProperties = isMinStay ? {
    color: '#c8c2b8',
    opacity: 0.6,
  } : isDisabled ? {
    color: '#9ca3af',
    textDecoration: 'line-through',
    textDecorationColor: '#9ca3af',
  } : {};

  const title = isMinStay
    ? "Doesn't meet the minimum stay"
    : isDisabled
      ? "Unavailable"
      : undefined;

  return (
    <span 
      className="relative flex items-center justify-center w-full h-full"
      style={styles}
      title={title}
      data-disabled={isDisabled ? "true" : undefined}
      data-min-stay={isMinStay ? "true" : undefined}
    >
      {date.getDate()}
    </span>
  );
}

/**
 * Enhanced Calendar component with Airbnb-like styling
 * - Clean, minimal design with smooth transitions
 * - Strikethrough styling for unavailable dates (rendered in JSX)
 * - Clear visual hierarchy
 * - Touch-optimized for mobile
 */
const Calendar = React.memo(function Calendar({
  className,
  classNames,
  showOutsideDays = false,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3 select-none w-full", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0 w-full",
        month: "space-y-4 w-full",
        caption: "flex justify-center pt-1 relative items-center h-10",
        caption_label: "text-base font-medium font-serif text-foreground",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-8 w-8 bg-transparent p-0 border-border/50",
          "opacity-70 hover:opacity-100 hover:bg-muted/80",
          "transition-all duration-150 ease-out transform-gpu",
          "active:scale-95 touch-manipulation rounded-full"
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse",
        head_row: "flex w-full justify-between",
        head_cell: cn(
          "text-muted-foreground rounded-md flex-1 max-w-[40px] font-normal text-xs font-serif",
          "py-2 text-center"
        ),
        row: "flex w-full justify-between mt-1",
        cell: cn(
          "h-9 w-9 max-w-[40px] flex-1 text-center text-sm p-0 relative",
          "transition-colors duration-75 ease-out",
          "[&:has([aria-selected].day-range-end)]:rounded-r-full",
          "[&:has([aria-selected].day-range-start)]:rounded-l-full",
          "[&:has([aria-selected])]:bg-sand/50",
          "focus-within:relative focus-within:z-20"
        ),
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 max-w-[40px] p-0 font-normal font-serif text-sm rounded-full",
          "hover:bg-muted/80 focus:bg-muted/80 focus:outline-none",
          "aria-selected:opacity-100",
          "transition-all duration-100 ease-out transform-gpu",
          "active:scale-95 touch-manipulation"
        ),
        day_range_start: cn(
          "day-range-start bg-ink text-white rounded-full",
          "hover:bg-ink/90 hover:text-white",
          "focus:bg-ink focus:text-white"
        ),
        day_range_end: cn(
          "day-range-end bg-ink text-white rounded-full",
          "hover:bg-ink/90 hover:text-white",
          "focus:bg-ink focus:text-white"
        ),
        day_selected: cn(
          "bg-ink text-white rounded-full",
          "hover:bg-ink/90 hover:text-white",
          "focus:bg-ink focus:text-white"
        ),
        day_today: cn(
          "text-ink font-semibold",
          "ring-1 ring-ink/30 ring-inset"
        ),
        day_outside: "day-outside text-muted-foreground/30 aria-selected:bg-sand/50 aria-selected:text-muted-foreground/50",
        // Disabled dates - gray text with strikethrough via CSS
        day_disabled: "rdp-day_disabled",
        day_range_middle: cn(
          "aria-selected:bg-sand/50 aria-selected:text-foreground rounded-none"
        ),
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ className, ...props }) => (
          <ChevronLeft className={cn("h-4 w-4", className)} {...props} />
        ),
        IconRight: ({ className, ...props }) => (
          <ChevronRight className={cn("h-4 w-4", className)} {...props} />
        ),
        DayContent: CustomDayContent,
      }}
      {...props}
    />
  )
})
Calendar.displayName = "Calendar"

export { Calendar }
